export type NotificationPayload = {
  scope?: string;
  sourceType?: string;
  message?: string;
  severity?: string;
  siteName?: string;
  siteAddress?: string;
  siteId?: number;
  alertType?: string;
  sound?: number;
  buttonAlertId?: number;
  deviceId?: number;
  deviceName?: string;
  latitude?: number;
  longitude?: number;
  /** ISO del backend (socket) para mostrar «Hace X min» como en la campana */
  timestamp?: string;
  /** Pulsaciones agrupadas (mismo sitio + tipo en ventana de 10 min) */
  pressCount?: number;
  /** false = no repetir audio en el VMS (reenvío agrupado) */
  playAlarmSound?: boolean;
  /** MP3 del stream ya guardado (notificación post stream-ended) */
  streamFinalized?: boolean;
  /** Marca única del MP3 (timestamp) para distinguir audios sucesivos de una misma alerta */
  streamAudioVersion?: number;
  mapNotificationId?: number;
  mapNotificationKind?: string;
  [key: string]: unknown;
};

export function notificationPayloadToNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Id de alerta en BD: el middleware a veces envía snake_case o variantes. */
export function pickButtonAlertId(src: Record<string, unknown>): number | undefined {
  const keys = ['buttonAlertId', 'button_alert_id', 'buttonAlertID', 'devices_button_history_id'] as const;
  for (const k of keys) {
    const v = notificationPayloadToNum(src[k]);
    if (v != null && v > 0) return v;
  }
  return undefined;
}

export function pickMapNotificationId(src: Record<string, unknown>): number | undefined {
  const keys = ['mapNotificationId', 'map_notification_id'] as const;
  for (const k of keys) {
    const v = notificationPayloadToNum(src[k]);
    if (v != null && v > 0) return v;
  }
  return undefined;
}

/** El middleware a veces envía el JSON anidado en `data` o `payload`. */
export function normalizeNotificationPayload(raw: unknown): NotificationPayload | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const nested =
    o.data && typeof o.data === 'object'
      ? (o.data as Record<string, unknown>)
      : o.payload && typeof o.payload === 'object'
        ? (o.payload as Record<string, unknown>)
        : null;
  const merged = nested ? { ...o, ...nested } : { ...o };
  const inner =
    merged.data && typeof merged.data === 'object' ? (merged.data as Record<string, unknown>) : null;
  const src = inner ? { ...merged, ...inner } : merged;

  let scope = typeof src.scope === 'string' ? src.scope.trim() : undefined;
  const sourceTypeRaw = typeof src.sourceType === 'string' ? src.sourceType : undefined;
  const buttonAlertId = pickButtonAlertId(src);
  const siteIdForInfer = notificationPayloadToNum(src.siteId);
  const soundForInfer = notificationPayloadToNum(src.sound);

  if (!scope) {
    const isMapButtonAlert =
      sourceTypeRaw === 'iot' ||
      buttonAlertId != null ||
      (siteIdForInfer != null &&
        (soundForInfer != null || typeof src.message === 'string' || buttonAlertId != null));
    if (isMapButtonAlert) {
      scope = 'map';
    }
  }

  if (!scope) return null;

  const latRaw = src.latitude;
  const lngRaw = src.longitude;
  const latitude = typeof latRaw === 'number' && Number.isFinite(latRaw) ? latRaw : undefined;
  const longitude = typeof lngRaw === 'number' && Number.isFinite(lngRaw) ? lngRaw : undefined;

  const sourceType = sourceTypeRaw ?? (scope === 'map' && buttonAlertId != null ? 'iot' : undefined);

  const timestampRaw = src.timestamp ?? src.createdAt;
  const timestamp =
    typeof timestampRaw === 'string' && timestampRaw.trim() !== '' ? timestampRaw : undefined;

  const pressRaw = src.pressCount ?? src.press_count;
  const pressN = notificationPayloadToNum(pressRaw);
  const pressCount =
    pressN != null && pressN >= 1 ? Math.min(9999, Math.floor(pressN)) : undefined;

  const playAlarmSound =
    src.playAlarmSound === false || src.play_alarm_sound === false ? false : undefined;

  const streamFinalized =
    src.streamFinalized === true ||
    src.stream_finalized === true ||
    src.streamAudioReady === true;

  const streamAudioVersion = notificationPayloadToNum(
    src.streamAudioVersion ?? src.stream_audio_version
  );

  const mapNotificationId = pickMapNotificationId(src);
  const mapNotificationKind =
    typeof src.mapNotificationKind === 'string' ? src.mapNotificationKind : undefined;

  return {
    scope,
    sourceType,
    message: typeof src.message === 'string' ? src.message : undefined,
    severity: typeof src.severity === 'string' ? src.severity : undefined,
    siteName: typeof src.siteName === 'string' ? src.siteName : undefined,
    siteAddress: typeof src.siteAddress === 'string' ? src.siteAddress : undefined,
    siteId: notificationPayloadToNum(src.siteId),
    alertType: typeof src.alertType === 'string' ? src.alertType : undefined,
    sound: notificationPayloadToNum(src.sound),
    buttonAlertId: pickButtonAlertId(src),
    deviceId: notificationPayloadToNum(src.deviceId),
    deviceName: typeof src.deviceName === 'string' ? src.deviceName : undefined,
    latitude,
    longitude,
    timestamp,
    ...(pressCount != null ? { pressCount } : {}),
    ...(playAlarmSound === false ? { playAlarmSound: false } : {}),
    ...(streamFinalized ? { streamFinalized: true } : {}),
    ...(streamAudioVersion != null ? { streamAudioVersion } : {}),
    ...(mapNotificationId != null ? { mapNotificationId } : {}),
    ...(mapNotificationKind ? { mapNotificationKind } : {}),
  };
}
